import { GetItemsProps } from 'medere-ui-framework'
import { axiosInstance } from '../App'
import {
  defaultKitDetailDTO,
  KitDetailDTO,
  KitItemDetailDTO,
  KitListItemDTO,
  KitsListQueryParams,
  KitSaveDTO,
  KitTypeOptionDTO,
  StockItemPresentationOptionDTO,
} from '../dto/kits/KitsDTO'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim()
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function parsePositiveNumber(value: unknown): number | null {
  const parsed = parseNumber(value)
  return parsed != null && parsed > 0 ? parsed : null
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}

function extractArray(rawData: unknown): unknown[] {
  if (Array.isArray(rawData)) return rawData
  const rawObj = toRecord(rawData)
  if (!rawObj) return []
  if (Array.isArray(rawObj.data)) return rawObj.data
  if (Array.isArray(rawObj.items)) return rawObj.items
  if (Array.isArray(rawObj.content)) return rawObj.content
  return []
}

function parseCreatedKitId(rawData: unknown): number {
  const direct = parsePositiveNumber(rawData)
  if (direct != null) return direct

  const rawObj = toRecord(rawData)
  if (rawObj) {
    const candidates = [rawObj.kitId, rawObj.kitid, rawObj.id, rawObj.value]
    for (const candidate of candidates) {
      const parsed = parsePositiveNumber(candidate)
      if (parsed != null) return parsed
    }
  }

  throw new Error('No se pudo obtener el id del kit guardado')
}

function trimToNull(value: string | null | undefined): string | null {
  const normalized = String(value ?? '').trim()
  return normalized !== '' ? normalized : null
}

function normalizeDecimalText(value: unknown): string {
  const normalized = String(value ?? '').trim().replace(',', '.')
  if (normalized === '') return ''

  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) return String(value ?? '').trim()

  const serialized = parsed.toString()
  return serialized.includes('.') ? serialized.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1') : serialized
}

function toKitTypeOption(item: unknown): KitTypeOptionDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const id = parsePositiveNumber(rawObj.id ?? rawObj.value ?? rawObj.stockItemsKitTypeId ?? rawObj.stockitemskittypeid)
  const name = toStringSafe(rawObj.name ?? rawObj.description ?? rawObj.label)
  if (id == null || name === '') return null

  return { id, name }
}

function toStockItemPresentationOption(item: unknown): StockItemPresentationOptionDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const id = parsePositiveNumber(rawObj.id ?? rawObj.value ?? rawObj.stockItemPresentationId ?? rawObj.stockitempresentationid)
  const name = toStringSafe(rawObj.name ?? rawObj.fullDescription ?? rawObj.label)
  if (id == null || name === '') return null

  return {
    id,
    name,
    stockItemId: parsePositiveNumber(rawObj.stockItemId ?? rawObj.stockitemid),
    presentationDescription: trimToNull(toStringSafe(rawObj.presentationDescription ?? rawObj.presentationdescription)),
    fullDescription: trimToNull(toStringSafe(rawObj.fullDescription ?? rawObj.fulldescription)) ?? name,
  }
}

function normalizeKitListItem(item: unknown): KitListItemDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const kitId = parsePositiveNumber(rawObj.kitId ?? rawObj.kitid ?? rawObj.stockitemskit)
  if (kitId == null) return null

  return {
    kitId,
    description: toStringSafe(rawObj.description),
    stockItemsKitTypeName: toStringSafe(rawObj.stockItemsKitTypeName ?? rawObj.stockitemskittypename ?? rawObj.kitTypeName ?? rawObj.kittypename),
    active: parseBoolean(rawObj.active, true),
  }
}

function normalizeKitItemDetail(item: unknown, index: number): KitItemDetailDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const stockItemPresentationId = parsePositiveNumber(rawObj.stockItemPresentationId ?? rawObj.stockitempresentationid)
  const itemLabel = toStringSafe(rawObj.itemLabel ?? rawObj.itemlabel ?? rawObj.fullDescription ?? rawObj.fulldescription)
  const quantity = normalizeDecimalText(rawObj.quantity)

  if (stockItemPresentationId == null || itemLabel === '') return null

  const stockItemPresentation = toStockItemPresentationOption({
    id: stockItemPresentationId,
    name: itemLabel,
    stockItemId: rawObj.stockItemId ?? rawObj.stockitemid,
    presentationDescription: rawObj.presentationDescription ?? rawObj.presentationdescription,
    fullDescription: rawObj.fullDescription ?? rawObj.fulldescription ?? itemLabel,
  })

  return {
    localKey: `persisted-${parsePositiveNumber(rawObj.stockItemsKitDetailId ?? rawObj.stockitemskitdetailid) ?? index}`,
    stockItemsKitDetailId: parsePositiveNumber(rawObj.stockItemsKitDetailId ?? rawObj.stockitemskitdetailid),
    stockItemPresentationId,
    itemLabel,
    quantity,
    mainItem: parseBoolean(rawObj.mainItem ?? rawObj.mainitem, false),
    stockItemPresentation,
  }
}

function normalizeKitDetail(rawData: unknown): KitDetailDTO {
  const rawObj = toRecord(rawData)
  if (!rawObj) return defaultKitDetailDTO

  return {
    kitId: parsePositiveNumber(rawObj.kitId ?? rawObj.kitid ?? rawObj.stockitemskit) ?? -1,
    description: toStringSafe(rawObj.description),
    active: parseBoolean(rawObj.active, true),
    observations: toStringSafe(rawObj.observations),
    stockItemsKitType: toKitTypeOption(rawObj.stockItemsKitType ?? rawObj.stockitemskittype),
    details: extractArray(rawObj.details).map((item, index) => normalizeKitItemDetail(item, index))
      .filter((item): item is KitItemDetailDTO => item !== null),
    deletedDetailIds: [],
  }
}

function extractEffectiveFilters(filterDTO: unknown, cardFilterDTO: unknown): unknown {
  const pick = (value: unknown): unknown | null => {
    const record = toRecord(value)
    if (!record) return null
    const filters = Array.isArray(record.filters) ? record.filters : []
    const filterLists = Array.isArray(record.filterLists) ? record.filterLists : []
    if (filters.length === 0 && filterLists.length === 0) return null
    return value
  }

  return pick(filterDTO) ?? pick(cardFilterDTO) ?? null
}

function toKitItemSave(detail: KitItemDetailDTO) {
  return {
    stockItemsKitDetailId: detail.stockItemsKitDetailId ?? null,
    stockItemPresentationId: detail.stockItemPresentationId as number,
    quantity: Number(String(detail.quantity).trim().replace(',', '.')),
    mainItem: detail.mainItem,
  }
}

export function getKitsList(
  offSet: number,
  pageSize: number,
  params: KitsListQueryParams = {},
): Promise<KitListItemDTO[]> {
  const filters = extractEffectiveFilters(params.filterDTO, params.cardFilterDTO)

  return axiosInstance.post('/api/secured/stock-items-kits/list', {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters,
    },
  }).then((response) => extractArray(response.data)
    .map((item) => normalizeKitListItem(item))
    .filter((item): item is KitListItemDTO => item !== null))
}

export function getKitById(kitId: number): Promise<KitDetailDTO> {
  return axiosInstance.get(`/api/secured/stock-items-kits/${kitId}`)
    .then((response) => normalizeKitDetail(response.data))
}

export function saveKit(payload: KitSaveDTO): Promise<number> {
  return axiosInstance.post('/api/secured/stock-items-kits/save', payload)
    .then((response) => parseCreatedKitId(response.data))
}

export function deleteKit(kitId: number): Promise<void> {
  return axiosInstance.delete(`/api/secured/stock-items-kits/${kitId}`)
    .then(() => undefined)
}

export function getKitTypes(): Promise<KitTypeOptionDTO[]> {
  return axiosInstance.get('/api/secured/stock-items-kits/types')
    .then((response) => extractArray(response.data)
      .map((item) => toKitTypeOption(item))
      .filter((item): item is KitTypeOptionDTO => item !== null))
}

export function getKitStockItemPresentations({ filterValue, offSet, pageSize }: GetItemsProps): Promise<StockItemPresentationOptionDTO[]> {
  return axiosInstance.get('/api/secured/stock-items-kits/stock-item-presentations', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    },
  }).then((response) => extractArray(response.data)
    .map((item) => toStockItemPresentationOption(item))
    .filter((item): item is StockItemPresentationOptionDTO => item !== null))
}

export function buildKitSavePayload(detail: KitDetailDTO): KitSaveDTO {
  return {
    kitId: detail.kitId > 0 ? detail.kitId : null,
    description: detail.description.trim(),
    active: detail.active,
    observations: detail.observations.trim(),
    stockItemsKitTypeId: detail.stockItemsKitType?.id as number,
    details: detail.details
      .filter((item) => item.stockItemPresentationId != null && item.stockItemPresentationId > 0)
      .map((item) => toKitItemSave(item)),
    deletedDetailIds: detail.deletedDetailIds.filter((id) => id > 0),
  }
}
